import { LobbyScene } from "../scenes/LobbyScene";
import { BroadcastScene } from "../scenes/BroadcastScene";
import { AppControllerImpl } from "./AppControllerImpl";
import { Logger } from "../utils/Logger";
import { AppView } from "../babylon/AppView";
import { NotificationCenterComponent } from "../babylon/components/NotificationCenterComponent";
import { LobbyView } from "../babylon/LobbyView";
import { BroadcastView } from "../babylon/BroadcastView";
import { Env } from "../settings/Env";
import { WebXRState } from "@babylonjs/core/XR/webXRTypes.js";
import { FontUtils } from "@dreamcam/babylon-tools";
import { overloadTextResourcesAsync } from "../TextResources";
import { loadGlobalOptionsAsync } from "../settings/GlobalOptions";
import { CompatibilityOptionsImpl } from "../settings/CompatibilityOptions";
import { BroadcastManagerState } from "@dreamcam/web-sdk";
import { FeatureManager, FeatureName } from "../settings/Features";
export class Core {
    get camera() {
        return AppView.getInstance().camera;
    }
    constructor(params) {
        this._isInitialized = false;
        this._scenesSwitcher = {
            setLobbyPageParams: (params) => {
                // setLobbyPageParams() clears broadcastPageParams!
                this._pageParams.lobbyPageParams = params;
                this._pageParams.broadcastPageParams = undefined;
                // console.log("%c++++ setLobbyPageParams => SitePageParams:", "background-color: yellow", this._pageParams);
            },
            setBroadcastPageParams: (params) => {
                // setBroadcastPageParams() doesn't clear lobbyPageParams!
                this._pageParams.broadcastPageParams = params;
                // console.log("%c++++ setBroadcastPageParams => SitePageParams:", "background-color: yellow", this._pageParams);
            },
            getSitePageParams: () => {
                return this._pageParams;
            },
            openLobbyAsync: async (params) => {
                this._pageParams.broadcastPageParams = undefined;
                if (params === undefined) {
                    params = {
                        lobbyMode: "Live",
                        // livePageIndex: 0
                    };
                }
                this._pageParams.lobbyPageParams = params;
                // console.log("%c++++ openLobbyAsync => SitePageParams:", "background-color: yellow", this._pageParams);
                this._appController.sdk.galleryManager.closeVideoClip();
                await this._appController.sdk.broadcastManager.leaveRoomAsync();
                await this._hideAllScenes();
                await this._lobbyScene.showAsync();
            },
            openBroadcastAsync: async (params) => {
                if (this._lobbyScene.isLoadingLivePage ||
                    this._lobbyScene.isLoadingVideosPage ||
                    this._lobbyScene.isLoadingPurchasesPage ||
                    this._lobbyScene.isUpdatingGalleryFilter)
                    return;
                await this._hideAllScenes();
                await this._broadcastScene.showAsync();
                this._pageParams.broadcastPageParams = params;
                this._pageParams.broadcastPageParams = await this._broadcastScene.openBroadcast(params);
            }
        };
        Env.setAssetsUrl(params.webXRParams?.assetsUrl);
        FontUtils.commonStyle = { fontFamily: Env.fontFamily };
        Logger.init(params.webXRParams.isReleaseMode);
        this._xrParams = params.webXRParams;
        CompatibilityOptionsImpl.getInstance().data = params.compatibilityOptions;
        FeatureManager.instance.setFeatures(params.features);
        // this._partnerTokenGenerator = params.partnerTokenGenerator;
        // this._pageParams = params.startPageParams;
        this._pageParams = params.sitePageParams ?? {};
        this._appController = new AppControllerImpl({
            scenesSwitcher: this._scenesSwitcher,
            hideScenesCallback: async () => {
                await this._broadcastScene.pauseAsync();
            },
            showScenesCallback: async () => {
                await this._broadcastScene.resumeAsync();
            },
            sdk: params.sdk,
            messengerSdk: params.messengerSdk,
            enableGtm: params.webXRParams.enableGtm,
            showHiddenBroadcasts: params.webXRParams.showHiddenBroadcasts,
            showOfflineBroadcasts: params.webXRParams.showOfflineBroadcasts,
            showCategoriesSelector: params.webXRParams.showCategoriesSelector !== false, // true by default
            displayedCategoriesArray: params.webXRParams.displayedCategoriesArray ?? [], // empty by default
        });
        this._lobbyScene = new LobbyScene(this._appController);
        this._lobbyScene.isManualFadeIn = true;
        this._broadcastScene = new BroadcastScene(this._appController);
        this._broadcastScene.isManualFadeIn = true;
        // this._allScenes.push(this._lobbyScene);
        // this._allScenes.push(this._broadcastScene);
        window.addEventListener("beforeunload", async () => {
            if (this.isXRMode) {
                await this.exitXRAsync();
            }
        });
    }
    static createInstance(params) {
        return new Core(params);
    }
    static async getAssetsVersionAsync(assetsUrl) {
        const assetsIndexResponse = await fetch(`${assetsUrl}/index.html`);
        const assetsIndexText = await assetsIndexResponse.text();
        const parser = new DOMParser();
        const document = parser.parseFromString(assetsIndexText, "text/html");
        const versionElement = document.getElementById("version");
        return versionElement?.textContent?.trim();
    }
    ;
    // noinspection JSUnusedGlobalSymbols
    async getAssetsVersionAsync() {
        return Core.getAssetsVersionAsync(this._xrParams.assetsUrl);
    }
    async initAsync() {
        if (this._isInitialized)
            return;
        this._isInitialized = true;
        FeatureManager.instance.setFeatureSupported(FeatureName.Passthrough, !this._xrParams.emulation && (await navigator.xr?.isSessionSupported("immersive-ar") ?? false));
        await this._hideAllScenes();
        await overloadTextResourcesAsync(this._xrParams.textsUrl);
        await loadGlobalOptionsAsync(this._xrParams.globalOptionsUrl);
        this._views = await this._initializeWebXRAsync(this._xrParams);
        AppView.getInstance().logo.show();
        // await overloadTextResourcesAsync(this._xrParams.textsUrl);
        // await loadGlobalOptionsAsync(this._xrParams.globalOptionsUrl);
        await this._lobbyScene.attachViewAsync(this._views?.lobbyView);
        await this._broadcastScene.attachViewAsync(this._views?.broadcastView);
        this._appController.notificationCenter = this._views?.notificationCenter;
        await this._appController.messengerCore.initAsync(this._views?.lobbyView, this._views?.broadcastView);
    }
    static async isXRModeSupportedAsync() {
        return await AppView.isXRModeSupportedAsync();
    }
    async _hideAllScenes() {
        if (this._lobbyScene.isVisible) {
            await this._lobbyScene.hideAsync();
        }
        if (this._broadcastScene.isVisible) {
            await this._broadcastScene.hideAsync();
        }
    }
    // createPlayer(params: { canvas: HTMLCanvasElement, video: HTMLVideoElement }): FlatScreen {
    //     return new FlatScreenImpl(params.canvas, params.video);
    // }
    get isXRMode() {
        return AppView._instance?.xrHelper?.baseExperience?.state === WebXRState.IN_XR;
    }
    async _initializeWebXRAsync(params) {
        if (!params)
            return undefined;
        const appView = new AppView({
            appController: this._appController,
            canvas: params.canvas,
            callbacks: params,
        });
        await appView.initWebXR(params.sixDoFMode, params.emulation);
        appView.xrHelper?.baseExperience.onStateChangedObservable.add(async (state) => {
            if (state === WebXRState.IN_XR) {
                await this.showSceneAsync();
            }
            else if (state === WebXRState.NOT_IN_XR) {
                await this.hideSceneAsync();
            }
        });
        return {
            notificationCenter: new NotificationCenterComponent(),
            lobbyView: new LobbyView(),
            broadcastView: new BroadcastView({ video: params.video })
        };
    }
    async showSceneAsync() {
        if (this._appController.sdk.broadcastManager.broadcast !== null && this._appController.sdk.broadcastManager.broadcastId !== null) {
            await this._broadcastScene.initAndShow();
        }
        else {
            await this._scenesSwitcher.openLobbyAsync(this._appController.scenesSwitcher.getSitePageParams().lobbyPageParams);
        }
        if (this._broadcastScene.isVisible) {
            await this._broadcastScene.resumeAsync();
        }
    }
    async hideSceneAsync() {
        await this._hideAllScenes();
    }
    async enterXRAsync(sitePageParams) {
        const sdk = this._appController.sdk;
        const bm = sdk.broadcastManager;
        if (bm.state === BroadcastManagerState.MY_PRIVATE_SHOW || bm.state === BroadcastManagerState.SPY_SHOW) {
            sdk.galleryManager.closeVideoClip();
            sitePageParams = {
                lobbyPageParams: {
                    lobbyMode: "Live",
                },
                broadcastPageParams: {
                    broadcastId: bm.broadcastId ?? "",
                    modelNickname: bm.broadcast?.modelNickname ?? "",
                },
            };
        }
        if (sitePageParams) {
            if (sitePageParams.lobbyPageParams) {
                this._appController.scenesSwitcher.setLobbyPageParams(sitePageParams.lobbyPageParams);
            }
            if (sitePageParams.broadcastPageParams) {
                this._appController.scenesSwitcher.setBroadcastPageParams(sitePageParams.broadcastPageParams);
            }
        }
        await this.initAsync();
        await AppView.getInstance().enterXRAsync();
    }
    async exitXRAsync() {
        await this._hideAllScenes();
        await AppView.getInstance().exitXRAsync();
    }
    async offerXRSessionAsync() {
        await this.initAsync();
        await AppView.getInstance().offerXRSessionAsync(true);
    }
    // noinspection JSUnusedGlobalSymbols
    get appController() {
        return this._appController;
    }
    // noinspection JSUnusedGlobalSymbols
    get views() {
        return this._views;
    }
}
